/**
 * Date display utilities for the dental-med app.
 * Rule: Store and process dates as YYYY-MM-DD (Firestore, Redux, Cloud Functions).
 * Only convert to DD/MM/YYYY (or DD.MM.YYYY) when rendering in the UI for Kosovo/Albanian users.
 */

/**
 * Converts a YYYY-MM-DD string from the database into DD/MM/YYYY for the UI (Kosovo standard).
 * @param {string} dateString - e.g. "2026-03-03"
 * @returns {string} - e.g. "03/03/2026", or '' if invalid
 */
export function formatForKosovoUI(dateString) {
  if (!dateString) return '';

  // Accept YYYY-MM-DD or ISO (YYYY-MM-DDTHH:mm:ss...) – use date part only
  const normalized = String(dateString).trim().slice(0, 10);
  const [year, month, day] = normalized.split('-');
  if (!year || !month || !day) return '';

  return `${day}/${month}/${year}`;
}

/**
 * Formats a YYYY-MM-DD string (or Date) for display using Intl (locale-aware).
 * Use this when you want consistent DD/MM/YYYY across the app with proper locale support.
 * @param {string|Date} dateInput - "2026-03-03" or a Date object
 * @param {string} [locale='en-GB'] - en-GB and sq-AL both use DD/MM/YYYY
 * @param {Object} [options] - Intl.DateTimeFormat options (default: day, month, year numeric)
 * @returns {string} - e.g. "03/03/2026" (en-GB) or "03.03.2026" (sq-AL)
 */
export function formatDateForLocale(dateInput, locale = 'en-GB', options = {}) {
  if (dateInput == null) return '';

  let date;
  if (typeof dateInput === 'string') {
    const trimmed = dateInput.trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return '';
    date = new Date(trimmed + 'T12:00:00.000Z');
    if (isNaN(date.getTime())) return '';
  } else if (dateInput instanceof Date) {
    date = dateInput;
    if (isNaN(date.getTime())) return '';
  } else {
    return '';
  }

  const defaultOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  };

  return new Intl.DateTimeFormat(locale, defaultOptions).format(date);
}

/**
 * Alias for Kosovo/Albanian UI: DD/MM/YYYY via Intl (sq-AL may use dot separator).
 * Pass a YYYY-MM-DD string from Redux/Firestore.
 */
export function formatForKosovoUIIntl(dateString) {
  return formatDateForLocale(dateString, 'sq-AL');
}

/**
 * Returns YYYY-MM-DD for the given date in Europe/Tirane (for filtering / comparisons).
 * @param {Date} [dateObj] - Defaults to now
 * @returns {string}
 */
export function getTiraneDateString(dateObj = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Tirane',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(dateObj);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Returns { start, end } as YYYY-MM-DD for the current month in Europe/Tirane.
 */
export function getThisMonthRangeTirane() {
  const today = getTiraneDateString();
  const [y, m] = today.split('-').map(Number);
  // Last day of month: day 0 of next month (month is 0-indexed in Date.UTC)
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    start: `${y}-${String(m).padStart(2, '0')}-01`,
    end: `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
}
